import * as React from 'react'
import {useCallback} from 'react'
import type {
  MeasureProps,
  MeasuredComponentProps,
  ContentRect,
  MeasurementType,
} from 'react-measure'
import getContentRect from 'react-measure/src/get-content-rect'
import getTypes from 'react-measure/src/get-types'

import {useSandbox} from '../utils/fastdom'

import useComposeRefs from './useComposeRefs'
import useLazy from './useLazy'

type MeasureSelectorResult<T> = {
  measureRef: React.RefCallback<Element>
  measure: (entries?: ResizeObserverEntry[]) => unknown
  result: T
}

export function useMeasureSelector<T>(
  props: MeasureProps,
  selector: (rect: ContentRect, state: T) => T,
  defaultValue: T,
): MeasureSelectorResult<T> {
  const {innerRef, onResize} = props
  const [result, updateResult] = React.useReducer(
    (state: T, rect: ContentRect) => selector(rect, state),
    defaultValue,
  )
  const ref = React.useRef<HTMLElement>(undefined)
  const sandbox = useSandbox()
  const joinedTypes = getTypes(props).join(',')
  const measure = React.useCallback(
    (entries?: ResizeObserverEntry[]) => {
      sandbox.measure(() => {
        const element = ref.current
        if (element == null) {
          return
        }

        const measurements = getContentRect(element, joinedTypes.split(',') as MeasurementType[])

        if (entries) {
          measurements.entry = entries[0].contentRect
        }

        sandbox.mutate(() => {
          updateResult(measurements)

          if (typeof onResize === 'function') {
            onResize(measurements)
          }
        })
      })
    },
    [onResize, joinedTypes, sandbox],
  )

  const createObserver = useCallback(() => new ResizeObserver(measure), [measure])
  const getObserver = useLazy(createObserver)
  const composeRefs = useComposeRefs()
  const measureRef = React.useCallback(
    (node: HTMLElement | null) =>
      composeRefs(innerRef, () => {
        const prevNode = ref.current
        if (prevNode === node) {
          return
        }

        const observer = getObserver()

        if (prevNode != null) {
          observer.unobserve(prevNode)
        }

        if (node != null && node instanceof HTMLElement) {
          ref.current = node
          measure()
          observer.observe(node)
        } else {
          observer.disconnect()
        }
      })(node),
    [composeRefs, getObserver, innerRef, measure],
  )

  return {
    result,
    measure,
    measureRef,
  }
}

export default function useMeasure(props: MeasureProps): MeasuredComponentProps {
  const {measure, measureRef, result} = useMeasureSelector(props, contentRect => contentRect, {
    entry: {},
    client: {},
    offset: {},
    scroll: {},
    bounds: {},
    margin: {},
  } as ContentRect)
  return {
    contentRect: result,
    measure,
    measureRef,
  }
}
